/**
 * Client for `xnotify-attribution`. FEAT-ATTRIB-003 `A2`.
 *
 * A separate service from the portal API, set via
 * `REACT_APP_ATTRIBUTION_API_URL`. The base URL is treated as ABSENT rather
 * than as a string when unset, because CRA inlines `process.env.*` at build
 * time and substitutes the literal `"undefined"` — so the naive form builds
 * into a request to `undefined/v1/attribution/summary`, a 404 against a host
 * that does not exist with nothing naming the missing variable. That is
 * `DA-BUG-ATTR-0219`, measured across 17 of this app's 35 `REACT_APP_*` vars,
 * and `src/config/envContract.test.js` is the guard.
 */

import axios from 'axios';

/**
 * The configured base URL, or `null`.
 *
 * `"undefined"` and `"null"` are checked as STRINGS. That is not paranoia:
 * they are exactly what CRA substitutes for an unset variable, and
 * `Boolean("undefined")` is `true`.
 */
export function attributionBaseUrl() {
  const raw = process.env.REACT_APP_ATTRIBUTION_API_URL;
  if (!raw) return null;
  const v = String(raw).trim();
  if (v === '' || v === 'undefined' || v === 'null') return null;
  let end = v.length;
  while (end > 0 && v[end - 1] === '/') end -= 1;
  return v.slice(0, end);
}

/** Whether the Impact tab can be shown at all. */
export const attributionConfigured = () => attributionBaseUrl() !== null;

function headers() {
  const h = {};
  const apiKey = localStorage.getItem('api-key');
  const accessToken = localStorage.getItem('customer-portal');
  if (apiKey) h['x-api-key'] = apiKey;
  if (accessToken) h['x-access-token'] = accessToken;
  return h;
}

/**
 * The §14 summary for one goal, optionally broken down by a dimension.
 *
 * Returns the service's payload UNCHANGED. Nothing here reshapes, rounds or
 * defaults a number: the panel's job is to display what the service
 * published, and a transform in the client is a second place for the figure
 * to be wrong. The one thing added is `ok: false` on a transport failure, so
 * the caller distinguishes "the service refused" from "the request failed".
 */
export async function fetchImpactSummary({ goalDefinitionId, dimension = 'channel', signal }) {
  const base = attributionBaseUrl();
  if (base === null) {
    return { ok: false, error: 'not_configured' };
  }

  try {
    const { data } = await axios.get(`${base}/v1/attribution/summary`, {
      params: { goal_definition_id: goalDefinitionId, dimension },
      headers: headers(),
      timeout: 20000,
      signal,
    });
    return data;
  } catch (err) {
    /**
     * A refusal from the service is a 4xx with its OWN body — a named
     * `error` such as `label_not_permitted_in_this_state`. That is data, not
     * a failure, and must reach the panel so it can say why.
     */
    const body = err?.response?.data;
    if (body && typeof body === 'object') return { ok: false, ...body };
    return { ok: false, error: 'request_failed', detail: err?.message ?? '' };
  }
}

/**
 * The per-tenant readiness profile for one goal. §6's `S2` surface.
 *
 * Same contract as `fetchImpactSummary`: the service's payload UNCHANGED, with
 * `ok: false` added only on a transport failure, so the panel can tell "the
 * service refused" from "the request failed". Nothing here reshapes, rounds or
 * defaults a figure — a transform in the client is a second place for the
 * number to be wrong.
 */
export async function fetchReadiness({ goalDefinitionId, signal }) {
  const base = attributionBaseUrl();
  if (base === null) {
    return { ok: false, error: 'not_configured' };
  }

  try {
    const { data } = await axios.get(`${base}/v1/attribution/readiness`, {
      params: { goal_definition_id: goalDefinitionId },
      headers: headers(),
      timeout: 20000,
      signal,
    });
    return data;
  } catch (err) {
    const body = err?.response?.data;
    if (body && typeof body === 'object') return { ok: false, ...body };
    return { ok: false, error: 'request_failed', detail: err?.message ?? '' };
  }
}
